import { Contact, NotificationChannel, Topic } from '@/core/notifications/model';
import ScopeSummary from './scope-summary';
import ChannelPicker from './channel-picker';
import InlineContactRow from './inline-contact-row';
import { useOpenWidget } from '@/core/widget-navigation/export';

// Step 3 of the subscription wizard: where the notification goes.
//
// Two entry modes:
// - `isPreload` (a module/pipeline deep-linked straight into this step): the
//   topic + scope were decided elsewhere, so they show as a fixed ScopeSummary
//   (the same reusable "fancy summary" the wizard's own scope-confirm step
//   uses) with a per-field Edit link that jumps straight to that field's own
//   step — never the whole creation flow. The Name — never asked for in this
//   flow — is collected right here, like SubscribeCreateForm did before it.
// - normal wizard flow: Name and Topic were Step 1, the scope was reviewed in
//   scope-confirm, so neither is repeated here.
export default function StepDelivery({
  channels,
  topic,
  isPreload,
  name,
  tagValues,
  scopeModeByField,
  missingRequiredTagKeys,
  invalidFieldKeys,
  selectedChannels,
  overrides,
  contacts,
  onNameChange,
  onToggleChannel,
  onOverrideChange,
  onContactAdded,
  onEditField,
}: {
  channels: NotificationChannel[];
  topic: Topic | undefined;
  isPreload: boolean;
  name: string;
  tagValues: Record<string, string[]>;
  scopeModeByField: Record<string, 'all' | 'narrow'>;
  missingRequiredTagKeys: string[];
  invalidFieldKeys: string[];
  selectedChannels: string[];
  overrides: Record<string, Record<string, unknown>>;
  contacts: Contact[];
  onNameChange: (name: string) => void;
  onToggleChannel: (channelKey: string) => void;
  onOverrideChange: (channelKey: string, config: Record<string, unknown>) => void;
  onContactAdded: (
    channelKey: string,
    contacts: Contact[] | undefined,
    addedContact: Contact,
  ) => void;
  onEditField: (tagKey: string) => void;
}) {
  const openWidget = useOpenWidget();

  return (
    <div>
      {isPreload && (
        <>
          <div className="mb-3">
            <label className="form-label" htmlFor="wizard-delivery-name">
              Name
            </label>
            <input
              id="wizard-delivery-name"
              className="form-control"
              value={name}
              onChange={event => onNameChange(event.target.value)}
            />
          </div>

          <div className="mb-3">
            <div className="mb-2" style={{ fontSize: 13 }}>
              <span className="fw-semibold">Topic:</span> {topic?.displayName ?? topic?.key ?? ''}
            </div>
            <ScopeSummary
              topic={topic}
              tagValues={tagValues}
              scopeModeByField={scopeModeByField}
              missingRequiredTagKeys={missingRequiredTagKeys}
              invalidFieldKeys={invalidFieldKeys}
              onEditField={onEditField}
            />
          </div>
        </>
      )}

      <div className="mb-3">
        <label className="form-label">Your delivery channels</label>
        <ChannelPicker
          channels={channels}
          selectedChannels={selectedChannels}
          overrides={overrides}
          contacts={contacts}
          onToggleChannel={onToggleChannel}
          onOverrideChange={onOverrideChange}
          renderAddContact={channelKey => {
            const channel = channels.find(c => c.key === channelKey);
            if (!channel) return null;
            return <InlineContactRow channel={channel} onAdded={onContactAdded} />;
          }}
          onManagePushDevices={() => openWidget('settings', 'notifications/contacts')}
          onManageContacts={() => openWidget('settings', 'notifications/contacts')}
        />
      </div>
    </div>
  );
}
